import { memo, useEffect, useMemo, useRef, useState } from "react";
import { SketchPicker } from "react-color";

import { Add } from "@material-ui/icons";
import { Button } from "antd";
import { MAX_RECENT_COLORS, cookies } from "constants/settings.constants";
import Cookies from "js-cookie";
import styled from "styled-components";
import { getStoredCustomColors } from "utils/color/getStoredCustomColors";

import { BaseRangeSlider } from "components/base";
import {
  FILL_PATTERN_DISPLAY_OPTIONS,
  generatePatternPreview
} from "components/map/utils/fillPatterns";
import {
  DEFAULT_FILL_PATTERN,
  DEFAULT_LINE_STYLE,
  type FillPattern,
  LINE_STYLE_DASH_ARRAYS,
  LINE_STYLE_OPTIONS,
  type LineStyle
} from "components/project/shapefiles/shapefiles.constants";

import "./ColorPicker.scss";

export interface IPinnedColor {
  color: string;
  label: string;
}

const color_grid_list = [
  [
    "transparent",
    "#FFFFFF",
    "#D0D4DD",
    "#B1B5BF",
    "#9498A2",
    "#777B87",
    "#5C606C",
    "#424652",
    "#292E3A",
    "#000000"
  ],
  [
    "#FF2825",
    "#FF9300",
    "#FFEB00",
    "#00B241",
    "#009988",
    "#00BFD8",
    "#0097FB",
    "#6F33BE",
    "#AB06B6",
    "#FE0061"
  ],
  [
    "#FFCBD1",
    "#FFDFAC",
    "#FFF9BD",
    "#C0E7C6",
    "#A6E1DB",
    "#A2EDF4",
    "#B2DFFE",
    "#E8BCEA",
    "#FFB8D1",
    "#FFBDD4"
  ],
  [
    "#FD9598",
    "#FFCA72",
    "#FFF590",
    "#97D8A2",
    "#66CDC4",
    "#5BE1EC",
    "#7FCBFE",
    "#B89BE0",
    "#D98FDD",
    "#FF88B2"
  ],
  [
    "#F56B6F",
    "#FFB42B",
    "#FFF25D",
    "#68CA7D",
    "#00B9AC",
    "#00D3E4",
    "#44B6FC",
    "#9C72D3",
    "#C760CE",
    "#FF5492"
  ],
  [
    "#FF4248",
    "#FFA300",
    "#FFEF26",
    "#3FBE60",
    "#00A99A",
    "#00C9DD",
    "#00A6FC",
    "#8652C9",
    "#B93AC2",
    "#FF237A"
  ],
  [
    "#E50922",
    "#FF7400",
    "#FFBE00",
    "#00912E",
    "#007B6B",
    "#009AAA",
    "#0076D9",
    "#5826AF",
    "#8705A8",
    "#D4005B"
  ],
  [
    "#C80009",
    "#F94200",
    "#FF7700",
    "#006013",
    "#004F40",
    "#006265",
    "#0046A7",
    "#371398",
    "#520492",
    "#950050"
  ]
];
const colors_with_border = ["transparent", "#FFFFFF", "#FFDFAC", "#FFF9BD"];
const transparent_color_selected_bg = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' version='1.1' preserveAspectRatio='none' viewBox='0 0 100 100'><path d='M0 99 L99 0 L100 1 L1 100' fill='rgb(214,40,11)' /></svg>")`;

interface ColorChooserProps {
  selectedColor: string;
  onChange: (color: string) => void;
  customColors: string[];
  setCustomColors: (colors: string[]) => void;
  includeOpacity?: boolean;
  onChangeOpacity?: ((opacity: number) => void) | null;
  selectedOpacity?: number;
  includeThickness?: boolean;
  minThickness?: number;
  onChangeThickness?: ((thickness: number) => void) | null;
  selectedThickness?: number;
  includeLineStyle?: boolean;
  onChangeLineStyle?: ((lineStyle: LineStyle) => void) | null;
  selectedLineStyle?: LineStyle;
  includeFillPattern?: boolean;
  onChangeFillPattern?: ((fillPattern: FillPattern) => void) | null;
  selectedFillPattern?: FillPattern;
  pinnedColor?: IPinnedColor;
  /** When true, the "transparent" swatch is visible but cannot be selected (wells/Focus Bins). */
  disableTransparent?: boolean;
}

function ColorChooser({
  selectedColor,
  onChange,
  customColors,
  setCustomColors,
  includeOpacity = false,
  onChangeOpacity = null,
  selectedOpacity = 100,
  includeThickness = false,
  minThickness = undefined,
  onChangeThickness = null,
  selectedThickness = 2,
  includeLineStyle = false,
  onChangeLineStyle = null,
  selectedLineStyle = DEFAULT_LINE_STYLE,
  includeFillPattern = false,
  onChangeFillPattern = null,
  selectedFillPattern = DEFAULT_FILL_PATTERN,
  pinnedColor = undefined,
  disableTransparent = false
}: ColorChooserProps) {
  const [screen, setScreen] = useState("list");
  const [customColor, setCustomColor] = useState(selectedColor);
  const [opacity, setOpacity] = useState(selectedOpacity);
  const [thickness, setThickness] = useState(selectedThickness);
  const [lineStyle, setLineStyle] = useState<LineStyle>(selectedLineStyle);
  const [fillPattern, setFillPattern] = useState<FillPattern>(selectedFillPattern);
  const screenTransitionTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => clearTimeout(screenTransitionTimer.current);
  }, []);

  const recentColors = useMemo(() => {
    const deduplicated = pinnedColor
      ? customColors.filter((c) => c.toLowerCase() !== pinnedColor.color.toLowerCase())
      : customColors;

    return deduplicated.slice(Math.max(deduplicated.length - MAX_RECENT_COLORS, 0));
  }, [customColors, pinnedColor]);

  function update(event) {
    const color = event["hex"];
    setCustomColor(color);
  }

  function saveCustomColor() {
    const storedCustomColors = Cookies.get(cookies.CUSTOM_COLORS);
    const savedColors = storedCustomColors
      ? getStoredCustomColors(storedCustomColors)
      : [];
    const updatedColors = [...savedColors, customColor];
    // js-cookie v3 no longer auto-JSON.stringifies array values like v2 did,
    // so this must be serialized explicitly or the cookie stores a bare
    // comma-joined string that fails JSON.parse on read (EVA-6897).
    Cookies.set(cookies.CUSTOM_COLORS, JSON.stringify(updatedColors));
    setCustomColors(updatedColors);
    // Defer the screen transition to the next tick so the "Add" button remains
    // in the DOM while the click event finishes propagating. Without this,
    // the button is unmounted synchronously during the click handler, and the
    // portal's document-level outside-click detector sees the (now-detached)
    // event target as outside the portal, causing the modal to close.
    // Note: queueMicrotask/Promise.then would NOT work here — microtasks drain
    // before the event finishes bubbling to document, so the detach still
    // happens mid-dispatch. setTimeout is the correct primitive.
    // Timer is stored in a ref and cleared on unmount to avoid a setState call
    // on an unmounted component in the unlikely event the modal closes first.
    screenTransitionTimer.current = setTimeout(() => setScreen("list"), 0);
  }

  return (
    <div>
      {screen === "list" && (
        <ColorChooserContainer>
          <FlexColumn>
            {color_grid_list.map((row, rowIdx) => (
              <FlexRow key={rowIdx}>
                {row.map((color) => {
                  const isTransparentDisabled =
                    color === "transparent" && disableTransparent;
                  return (
                    <ColorSquare
                      key={color}
                      backgroundColor={color}
                      isDisabled={isTransparentDisabled}
                      className={`${
                        color.toLocaleLowerCase() === selectedColor ? "selected" : ""
                      }`}
                      onClick={isTransparentDisabled ? undefined : () => onChange(color)}
                      data-testid="default-color-button"
                    />
                  );
                })}
              </FlexRow>
            ))}
            <FlexRow className="justify-between" style={{ marginTop: "10px" }}>
              <span>Custom colours</span>
              <CustomColorBtn onClick={() => setScreen("custom")}>
                <Add style={{ fontSize: "18px", color: "#A2AAAD" }}></Add>
              </CustomColorBtn>
            </FlexRow>
            <FlexRow>
              {pinnedColor && (
                <ColorSquare
                  backgroundColor={pinnedColor.color}
                  className={`${
                    pinnedColor.color.toLocaleLowerCase() === selectedColor
                      ? "selected"
                      : ""
                  }`}
                  onClick={() => onChange(pinnedColor.color)}
                  title={pinnedColor.label}
                  data-testid="pinned-color-button"
                />
              )}
              {recentColors.map((color) => (
                <ColorSquare
                  key={color}
                  backgroundColor={color}
                  className={`${
                    color.toLocaleLowerCase() === selectedColor ? "selected" : ""
                  }`}
                  onClick={() => onChange(color)}
                />
              ))}
            </FlexRow>
            {includeOpacity ? (
              <FlexRow className="justify-between" style={{ marginTop: "10px" }}>
                <BaseRangeSlider
                  value={Math.round(opacity)}
                  label="Opacity"
                  max={100}
                  unit="%"
                  onChange={(val) => {
                    setOpacity(val);
                    if (val) {
                      const o = Number(val) / 100.0;
                      onChangeOpacity && onChangeOpacity(o);
                    }
                  }}
                />
              </FlexRow>
            ) : null}
            {includeThickness ? (
              <FlexRow className="justify-between" style={{ marginTop: "10px" }}>
                <BaseRangeSlider
                  value={thickness}
                  label="Thickness"
                  min={minThickness}
                  max={10}
                  step={0.5}
                  unit="pt"
                  onChange={(val) => {
                    setThickness(val);
                    if (val) {
                      onChangeThickness && onChangeThickness(Number(val));
                    }
                  }}
                />
              </FlexRow>
            ) : null}
            {includeLineStyle ? (
              <FlexColumn style={{ marginTop: "10px" }}>
                <LineStyleLabel>Line Style</LineStyleLabel>
                <LineStyleOptionsRow data-testid="line-style-select">
                  {LINE_STYLE_OPTIONS.map((opt) => {
                    const dashArray = LINE_STYLE_DASH_ARRAYS[opt.value];
                    const strokeColor =
                      selectedColor === "transparent" ? "#000" : selectedColor;
                    return (
                      <LineStyleOption
                        key={opt.value}
                        className={lineStyle === opt.value ? "selected" : ""}
                        onClick={() => {
                          setLineStyle(opt.value);
                          onChangeLineStyle?.(opt.value);
                        }}
                        title={opt.label}>
                        <svg
                          width="100%"
                          height="16"
                          viewBox="0 0 80 16"
                          preserveAspectRatio="xMidYMid meet">
                          <line
                            x1="4"
                            y1="8"
                            x2="76"
                            y2="8"
                            stroke={strokeColor}
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            {...(dashArray
                              ? { strokeDasharray: dashArray.join(",") }
                              : {})}
                          />
                        </svg>
                      </LineStyleOption>
                    );
                  })}
                </LineStyleOptionsRow>
              </FlexColumn>
            ) : null}
            {includeFillPattern ? (
              <FlexColumn style={{ marginTop: "10px" }}>
                <LineStyleLabel>Fill Pattern</LineStyleLabel>
                <FillPatternOptionsGrid data-testid="fill-pattern-select">
                  <FillPatternOption
                    className={fillPattern === "none" ? "selected" : ""}
                    onClick={() => {
                      setFillPattern("none");
                      onChangeFillPattern?.("none");
                    }}
                    title="None">
                    <span style={{ fontSize: "11px" }}>None</span>
                  </FillPatternOption>
                  {FILL_PATTERN_DISPLAY_OPTIONS.map((opt) => {
                    const previewColor =
                      selectedColor === "transparent" ? "#000" : selectedColor;
                    return (
                      <FillPatternOption
                        key={opt.value}
                        className={fillPattern === opt.value ? "selected" : ""}
                        onClick={() => {
                          setFillPattern(opt.value);
                          onChangeFillPattern?.(opt.value);
                        }}
                        title={opt.label}>
                        <PatternPreview
                          style={{
                            backgroundImage: `url(${generatePatternPreview(
                              opt.value,
                              previewColor,
                              20
                            )})`,
                            backgroundRepeat: "repeat",
                            backgroundSize: "20px 20px"
                          }}
                        />
                      </FillPatternOption>
                    );
                  })}
                </FillPatternOptionsGrid>
              </FlexColumn>
            ) : null}
          </FlexColumn>
        </ColorChooserContainer>
      )}
      {screen === "custom" && (
        <CustomColorContainer className="customColorContainer">
          <SketchPicker
            color={customColor}
            onChange={update}
            disableAlpha
            presetColors={[]}
          />
          <StyledButton type="primary" size="small" onClick={saveCustomColor}>
            Add
          </StyledButton>
        </CustomColorContainer>
      )}
    </div>
  );
}

export default memo(ColorChooser);

const FlexRow = styled.div`
  display: inline-flex;
  align-items: center;
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const ColorChooserContainer = styled.div`
  display: flex;

  & > div > div:nth-child(2) {
    margin-bottom: 5px;
  }
`;

const CustomColorContainer = styled.div`
  display: grid;
  gap: 12px;
  font-family: var(--fontFamily) !important;

  .chrome-picker {
    box-shadow: none !important;
    & > div:nth-child(2) > div:nth-child(2) > div:nth-child(2) {
      display: none;
    }
    label {
      display: none !important;
    }
  }

  .sketch-picker {
    display: grid;
    grid-template-columns: 35px 1fr;
    grid-template-areas: "SB-Selector SB-Selector" ". colorSlider" ". colorInputs";

    & > div:first-child {
      grid-area: SB-Selector;
      border-radius: var(--border-radius);

      .saturation-white {
        border-radius: var(--border-radius);
        box-shadow: rgba(0, 0, 0, 0.15) 0px 0px 0px 1px inset,
          rgba(0, 0, 0, 0.25) 0px 0px 4px inset;
      }

      .saturation-black {
        border-radius: var(--border-radius);
      }
    }

    //selects container for color slider and preview box
    & > div:nth-child(2) {
      grid-area: colorSlider;
      flex-direction: row-reverse;

      //selects the color slider
      & > div:first-child {
        padding: 8px 0 4px !important;

        & > div:first-child {
          border-radius: 2px;
        }
      }

      //selects the color preview box
      & > div:nth-child(2) {
        position: absolute !important;
        left: -35px;
        margin-top: 8px !important;
        margin-left: 0 !important;
        height: 56px !important;
        width: 27px !important;
        border-radius: 2px !important;
        overflow: hidden;
      }
    }

    //selects the input boxes
    & > div:nth-child(3) {
      grid-area: colorInputs;

      input {
        width: 100% !important;
        text-align: center;
        border-radius: var(--border-radius);
      }
    }
  }
`;

const StyledButton = styled(Button)`
  justify-self: center;
`;

const ColorSquare = styled.div<{ backgroundColor: string; isDisabled?: boolean }>`
  width: 18px;
  height: 18px;
  background-color: ${(props) => props.backgroundColor};
  border: ${(props) =>
    colors_with_border.includes(props.backgroundColor) ? "1px solid #D9E1E2" : "none"};
  border-radius: 4px;
  margin: 2px;
  cursor: ${(props) => (props.isDisabled ? "not-allowed" : "pointer")};
  background-image: ${(props) =>
    props.backgroundColor === "transparent" ? transparent_color_selected_bg : "none"};

  &:hover,
  &.selected {
    box-shadow: ${(props) =>
      props.isDisabled
        ? "none"
        : `${
            ["#FFFFFF", "transparent"].includes(props.backgroundColor)
              ? "#D0D4DD"
              : props.backgroundColor
          } 0px 0px 3px`};
  }
`;

const CustomColorBtn = styled.div`
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d9e1e2;
  cursor: pointer;
`;

const LineStyleLabel = styled.span`
  flex: 1 1 auto;
  width: 100%;
  color: var(--ink);
  margin-bottom: 4px;
`;

const LineStyleOptionsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
`;

const LineStyleOption = styled.div`
  flex: 1;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d9e1e2;
  border-radius: 4px;
  cursor: pointer;
  background: var(--color-bg);
  padding: 0 4px;

  &:hover {
    border-color: var(--color-primary);
  }

  &.selected {
    border-color: var(--color-primary);
    background: var(--color-primary-light, rgba(0, 151, 251, 0.08));
  }
`;

const FillPatternOptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
`;

const FillPatternOption = styled.div`
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d9e1e2;
  border-radius: 4px;
  cursor: pointer;
  background: var(--color-bg);
  overflow: hidden;

  &:hover {
    border-color: var(--color-primary);
  }

  &.selected {
    border-color: var(--color-primary);
    background: var(--color-primary-light, rgba(0, 151, 251, 0.08));
  }
`;

const PatternPreview = styled.div`
  width: 100%;
  height: 100%;
`;
